import { API_BASE_URL, fetchWithTimeout, handleResponse } from "./shared";

// ============================================================================
// Strategy Pricing Admin API (SFWL-014 — retail/wholesale/reseller tiers)
// ============================================================================

/**
 * Entire `/api/v1/admin/strategies/:strategyId/pricing` surface is
 * `SovrunAdminGuard`-gated server-side (mirrors block-accounts), 403 for
 * non-`sovrun_admin` sessions on both GET and PUT. Amounts are decimal
 * strings end-to-end — never coerce with `parseFloat`, the resolver computes
 * `amountAfterPromo` server-side in integer cents.
 */

export type StrategyPricingTier = "retail" | "wholesale" | "reseller_retail";

export interface StrategyPricingPromo {
  code: string;
  discountPct: string;
  expiresAt: string;
}

/** One reseller override as returned by GET, inlined on its pricing version. */
export interface StrategyPricingOverrideDto {
  id: string;
  tenantId: string;
  resellerRetailPriceMonthly: string;
  promo: StrategyPricingPromo | null;
}

/** One `strategy_pricing` version (live or historical). */
export interface StrategyPricingVersionDto {
  id: string;
  strategyId: string;
  retailPriceMonthly: string;
  wholesalePriceMonthly: string;
  currency: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  overrides: StrategyPricingOverrideDto[];
}

/** `GET .../pricing` response — live version plus full history (DESC). */
export interface StrategyPricingAdminResponse {
  current: StrategyPricingVersionDto | null;
  history: StrategyPricingVersionDto[];
}

/** One override in the PUT body's full desired set (promo all-or-none). */
export interface SetPricingOverrideRequest {
  tenantId: string;
  resellerRetailPriceMonthly: string;
  promo?: { code: string; discountPct: string; expiresAt: string } | null;
}

/**
 * `PUT .../pricing` request body — base prices for the new version + the
 * COMPLETE desired override set (full-replace, not a patch; omitting a
 * tenant here removes its override from the new live version).
 */
export interface UpdateStrategyPricingRequest {
  retailPriceMonthly: string;
  wholesalePriceMonthly: string;
  currency?: string;
  overrides: SetPricingOverrideRequest[];
}

const STRATEGY_PRICING_BASE = `${API_BASE_URL}/api/v1/admin/strategies`;

export const strategyPricingApi = {
  /** Live version + full history (ordered `effectiveFrom DESC`) for one strategy. */
  async getStrategyPricing(strategyId: string): Promise<StrategyPricingAdminResponse> {
    const response = await fetchWithTimeout(
      `${STRATEGY_PRICING_BASE}/${strategyId}/pricing`
    );
    return handleResponse<StrategyPricingAdminResponse>(response);
  },

  /**
   * Close-and-insert: closes the live version and inserts a new one with the
   * submitted base prices + the full override set. 409 (message contains
   * "updated concurrently") on a concurrent-edit race or a duplicate
   * `tenantId` in the submitted override array — caller should refetch and
   * retry.
   */
  async setStrategyPricing(
    strategyId: string,
    data: UpdateStrategyPricingRequest
  ): Promise<StrategyPricingVersionDto> {
    const response = await fetchWithTimeout(
      `${STRATEGY_PRICING_BASE}/${strategyId}/pricing`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
    return handleResponse<StrategyPricingVersionDto>(response);
  },
};
